/**
 * EquityCurveChart — Strategy vs Benchmark equity evolution.
 */
import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border p-2 shadow-lg text-xs">
      <p className="font-mono text-muted-foreground mb-1">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span style={{ color: p.color }}>●</span>
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-mono font-bold text-foreground">{Number(p.value).toFixed(4)}</span>
        </div>
      ))}
    </div>
  );
};

const EquityCurveChart = ({ curve = [], metrics = null }) => {
  if (!curve.length) {
    return (
      <div className="bg-surface border border-border p-8 text-center text-sm text-muted-foreground">
        No backtest data yet. Run a backtest to see the equity curve.
      </div>
    );
  }

  const data = curve.map((pt) => ({
    date: pt.date,
    strategy: pt.equity,
    benchmark: pt.benchmark,
  }));

  return (
    <div className="bg-surface border border-border p-4" data-testid="equity-curve">
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <MetricCard
            label="Total Return"
            value={`${metrics.total_return_pct}%`}
            subvalue={`vs ${metrics.benchmark_return_pct}% bench`}
            positive={metrics.total_return_pct > 0}
          />
          <MetricCard
            label="CAGR"
            value={`${metrics.cagr_pct}%`}
            subvalue={`vs ${metrics.benchmark_cagr_pct}% bench`}
            positive={metrics.cagr_pct > 0}
          />
          <MetricCard
            label="Sharpe"
            value={metrics.sharpe_ratio}
            subvalue="annualized"
            positive={metrics.sharpe_ratio > 1}
          />
          <MetricCard
            label="Max Drawdown"
            value={`-${metrics.max_drawdown_pct}%`}
            subvalue="worst peak-to-trough"
            positive={metrics.max_drawdown_pct < 15}
          />
          <MetricCard
            label="Alpha"
            value={`${metrics.alpha_pct > 0 ? '+' : ''}${metrics.alpha_pct}%`}
            subvalue="vs NIFTY50"
            positive={metrics.alpha_pct > 0}
          />
          <MetricCard
            label="Win Rate"
            value={`${metrics.win_rate_pct}%`}
            subvalue={`${metrics.num_months} months`}
            positive={metrics.win_rate_pct > 50}
          />
          <MetricCard
            label="Best Month"
            value={`+${metrics.best_month_pct}%`}
            positive
          />
          <MetricCard
            label="Worst Month"
            value={`${metrics.worst_month_pct}%`}
            positive={false}
          />
        </div>
      )}

      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
            <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10 }} stroke="#30363d" />
            <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} stroke="#30363d" />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '11px' }}
              iconType="line"
            />
            <Line
              type="monotone"
              dataKey="strategy"
              name="AlphaScout"
              stroke="#58a6ff"
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="benchmark"
              name="NIFTY50"
              stroke="#fbbf24"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const MetricCard = ({ label, value, subvalue, positive }) => (
  <div className="bg-surface-highlight border border-border/50 p-2.5 rounded-sm">
    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
      {label}
    </div>
    <div
      className={`text-lg font-bold font-mono ${
        positive === true ? 'text-emerald-400' : positive === false ? 'text-red-400' : 'text-foreground'
      }`}
    >
      {value}
    </div>
    {subvalue && (
      <div className="text-[10px] text-muted-foreground mt-0.5">{subvalue}</div>
    )}
  </div>
);

export default EquityCurveChart;
